<template>
  <section class="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-md w-full space-y-8 bg-white p-10 rounded-xl shadow-lg">
      <form class="space-y-6" @submit.prevent="onLogin">
        <div class="text-center">
          <img :src="portalLogo" alt="Mediq logo" class="w-48 mx-auto drop-shadow-md" />
        </div>

        <div v-if="environment === 'tst' || environment === 'acc'">
          <Alert
            :html="true"
            htmlMessage="Dit is een test omgeving van het voorschrijfportaal. Als zorg professional kunt u hier niet inloggen.
                          <p class='underline cursor-pointer'><a href='https://voorschrijver.mediq.nl/'>Klik hier om naar de juiste omgeving te gaan.</a></p>"
            type="alert"
            class="z-40"
          ></Alert>
        </div>

        <!-- Login Fields -->
        <div v-if="!showAuthenticationField" class="space-y-4">
          <v-input-group-simple-2
            id="username"
            v-model="username"
            class="input-field"
            type="text"
            label="Voorschrijver Gebruikersnaam"
            data-vv-as="Gebruikersnaam"
            placeholder="Uw gebruikersnaam"
            :rules="{ required: true }"
          />

          <v-input-group-simple-2
            id="password"
            :key="inputId"
            v-model="password"
            :show-hide-password-icon="true"
            class="input-field"
            :type="passwordType"
            label="Wachtwoord"
            data-vv-as="Wachtwoord"
            placeholder="Uw wachtwoord"
            :rules="{ required: true }"
          />
        </div>

        <!-- 2FA Fields -->
        <div v-if="showAuthenticationField" class="space-y-4">
          <p>
            <b>{{ authenticationConfiguration.title2Fa }}</b>
          </p>
          <p>
            <b>{{ authenticationConfiguration.description2Fa }}</b>
          </p>
          <div class="verification">
            <p class="mb-2">
              {{ authenticationConfiguration.label2Fa }}
            </p>
            <v-input-group-simple-2
              id="verificationCode"
              v-model="verificationCode"
              class="input"
              maxlength="6"
              placeholder="Uw verificatiecode"
              data-vv-as="verificatie code"
              :rules="{
                required: true
              }"
            />
            <p class="my-2" v-if="invalidVerificationCode">Verificatie code opnieuw verzonden</p>
            <div class="grid grid-rows">
              <button
                @click.stop.prevent="resendVerificationCode()"
                class="flex justify-center py-2 px-2 md:px-4 border border-primary-color shadow-sm text-xs md:text-sm lg:text-sm font-medium text-primary-color bg-white rounded-full hover:bg-primary-default/50 items-center mt-4"
              >
                <span class="">{{ authenticationConfiguration.resend2FaCodeButtonLabel }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex flex-col space-y-4">
          <button
            id="loginButton"
            type="submit"
            class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-pdsPrimaryColor hover:bg-pdsPrimaryColor/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pdsPrimaryColor disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="isLoading" class="loader"></span>
            <span v-else>Inloggen</span>
          </button>

          <div v-if="!showAuthenticationField" class="text-center">
            <router-link
              :to="{ name: 'herstelwachtwoord' }"
              class="text-sm font-medium text-pdsPrimaryColor hover:text-pdsPrimaryColor/80"
            >
              Wachtwoord vergeten
            </router-link>
          </div>
        </div>

        <!-- Error Messages -->
        <div v-if="!showAuthenticationField" class="mt-4">
          <p v-if="invalidUsername || (invalidPassword && invalidUsername)" class="text-sm text-red-600">
            Deze gebruikersnaam is niet bekend, controleer uw gegevens.
          </p>
          <p v-if="invalidPassword && !invalidUsername" class="text-sm text-red-600">
            Uw wachtwoord is niet correct. Probeer het nogmaals of klik op 'Wachtwoord vergeten'.
          </p>
        </div>
        <hr />
        <!-- Registration Link -->
        <div v-if="!showAuthenticationField" class="text-center mt-6">
          <p class="text-sm text-gray-600 font-medium">Nog niet geregistreerd?</p>
          <div
            class="mt-2 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-pdsPrimaryColor bg-gray-100 hover:bg-gray-200"
          >
            <router-link
              :to="{ name: 'prescriberRegistration' }"
              class="text-sm font-medium text-pdsPrimaryColor hover:text-pdsPrimaryColor/80"
            >
              Registreren als gebruiker
            </router-link>
          </div>
        </div>
      </form>
    </div>
  </section>
</template>

<script>
import { useForm } from 'vee-validate';

import {
  formLogin,
  isLoggedIn$,
  authenticationConfiguration$,
  userNotLoggedInEmptyCodeEvent$,
  userNotLoggedInInvalidUsernameEvent$,
  userNotLoggedInInvalidPasswordEvent$,
  userNotLoggedInInvalidCodeEvent$,
  storeForceSecurityPolicySelection
} from '@/services';
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';

export default {
  name: 'UsernamePasswordLogin',
  components: {},
  setup() {
    const store = useStore();
    const route = useRoute();
    const username = ref(null);
    const password = ref(null);
    const verificationCode = ref('');
    const isLoggedIn = ref(false);
    const invalidVerificationCode = ref(null);
    const invalidPassword = ref(false);
    const invalidUsername = ref(false);
    const showAuthenticationField = ref(false);
    const authenticationConfiguration = ref(null);
    const isLoading = ref(false);

    const portalLogo = computed(() => store.getters['navigation/getPortalLogo']);
    const registrationDetails = computed(() => store.getters['navigation/getRegistrationDetails']);
    const environment = computed(() => store.getters['navigation/getEnvironment']);

    const subscriptions = [];
    // Manage subscriptions
    onMounted(() => {
      subscriptions.push(
        isLoggedIn$.subscribe((value) => {
          isLoggedIn.value = value;
        }),
        userNotLoggedInInvalidCodeEvent$.subscribe((value) => {
          invalidVerificationCode.value = true;
        }),
        userNotLoggedInEmptyCodeEvent$.subscribe((value) => {
          showAuthenticationField.value = true;
        }),
        authenticationConfiguration$.subscribe((value) => {
          authenticationConfiguration.value = value;
        }),
        userNotLoggedInInvalidUsernameEvent$.subscribe((value) => {
          invalidUsername.value = value;
          if (value) {
            invalidUsername.value = true;
          }
        }),
        userNotLoggedInInvalidPasswordEvent$.subscribe((value) => {
          invalidPassword.value = value;
          if (value) {
            invalidUsername.value = false;
            invalidPassword.value = true;
          }
        })
      );

      document.addEventListener('onautocomplete', function (e) {
        if (e.target.id === 'username' && e.target.hasAttribute('autocompleted')) {
          document.getElementById('loginButton').classList.remove('btn--disabled');
        }
      });
    });

    onBeforeUnmount(() => {
      subscriptions.forEach((sub) => sub.unsubscribe());
    });

    return {
      username,
      password,
      verificationCode,
      portalLogo,
      registrationDetails,
      isLoggedIn,
      invalidVerificationCode,
      invalidPassword,
      invalidUsername,
      showAuthenticationField,
      authenticationConfiguration,
      isLoading,
      environment
    };
  },
  data() {
    const { validate } = useForm();
    return {
      validate,
      passwordType: 'password',
      inputId: 0
    };
  },
  watch: {
    username(value) {
      if (value && this.password) {
        document.getElementById('loginButton').classList.remove('btn--disabled');
      } else {
        document.getElementById('loginButton').classList.add('btn--disabled');
      }
    },
    password(value) {
      if (value && this.username) {
        document.getElementById('loginButton').classList.remove('btn--disabled');
      } else {
        document.getElementById('loginButton').classList.add('btn--disabled');
      }
    },
    verificationCode(value) {
      if (value && value.length > 0) {
        this.invalidVerificationCode = false;
      }
    }
  },
  mounted() {
    document.addEventListener('onautocomplete', function (e) {
      if (e.target.id === 'username' && e.target.hasAttribute('autocompleted')) {
        document.getElementById('loginButton').classList.remove('btn--disabled');
      }
    });

    // Check for token in URL and trigger tokenLogin if present
    if (this.$route.query.token) {
      this.tokenLogin();
      localStorage.setItem('tokenlogin', 'true');
    }
  },
  methods: {
    async tokenLogin() {
      await formLogin('', '', '', this.$route.query.token);
    },
    async onLogin() {
      this.isLoading = true;
      try {
        if (this.showAuthenticationField) {
          const result = await this.validate();
          if (result.valid) {
            await formLogin(this.username, this.password, this.verificationCode);
          }
        } else {
          await formLogin(this.username, this.password);
        }
        storeForceSecurityPolicySelection(true);
      } catch (error) {
        // Handle login failure
      } finally {
        this.isLoading = false;
      }
    },
    resendVerificationCode() {
      formLogin(this.username, this.password);
    }
  }
};
</script>

<style scoped>
.loader {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #006983;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  animation: spin 1s linear infinite;
  margin: auto;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
